import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth/auth-context';
import { completeLogin, SignInError } from '@/lib/auth/keycloak';
import { Column, Grid, Loading, InlineNotification } from '@carbon/react';

/**
 * Auth callback route: exchanges the Keycloak `?code=` for tokens (PKCE, in
 * memory) and lands on the page the user asked for. No token ever arrives in
 * the URL any more — the BFF issues none.
 */

/**
 * `/auth`, carrying the route to come back to once credentials are given.
 *
 * Only in-app paths travel, and never an auth route: `returnTo` ends up in a redirect, so an absolute
 * URL here would make this an open redirector, and `/auth` would make the user bounce between the two
 * pages. Anything else is dropped rather than sanitised, because a half-trusted redirect target is the
 * kind of thing that becomes a vulnerability the day somebody "improves" it.
 */

/**
 * Where to land once the tokens are in hand.
 *
 * Unwraps a nested `/auth?returnTo=…` instead of falling back to the home page. A transaction recorded
 * while the browser sat on `/auth` used to read `returnTo=/auth?returnTo=%2Feditor`, this function
 * refused it (rightly: landing on an auth route loops), and the deep link became "/". Refusing the loop
 * is correct; throwing the destination away was not.
 */
export function landingRouteFor(returnTo: string): string {
  if (!returnTo.startsWith('/') || returnTo.startsWith('//')) return '/';
  if (!returnTo.startsWith('/auth')) return returnTo;
  const nested = new URLSearchParams(returnTo.split('?')[1] || '').get('returnTo');
  if (!nested) return '/';
  return landingRouteFor(nested);
}

export function authRouteFor(returnTo?: string): string {
  if (!returnTo) return '/auth';
  const isAppPath = returnTo.startsWith('/') && !returnTo.startsWith('//');
  if (!isAppPath || returnTo.startsWith('/auth')) return '/auth';
  return `/auth?returnTo=${encodeURIComponent(returnTo)}`;
}
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { onTokensAcquired } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // React 18 StrictMode mounts twice; the authorization code is single-use.
    if (started.current) return;
    started.current = true;

    (async () => {
      try {
        const { tokens, returnTo } = await completeLogin(new URLSearchParams(window.location.search));
        await onTokensAcquired(tokens);
        const target = landingRouteFor(returnTo);
        navigate(target, { replace: true });
      } catch (err) {
        // A silent (prompt=none) attempt that fails is NOT an error to show:
        // it only means there is no live SSO session. Go ask for credentials — and CARRY the route the
        // user asked for, because this is where it used to die: the interactive login that follows
        // would otherwise record `returnTo=/auth`, which this same function refuses below, so every
        // deep link ended on `/`.
        if (err instanceof SignInError && err.silent) {
          navigate(authRouteFor(err.returnTo), { replace: true });
          return;
        }
        setError(err instanceof Error ? err.message : 'Authentication failed. Please try again.');
        setTimeout(() => navigate('/auth', { replace: true }), 4000);
      }
    })();
  }, [onTokensAcquired, navigate]);

  if (error) {
    return (
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <InlineNotification kind="error" title="Authentication Error" subtitle={error} hideCloseButton />
          </div>
        </Column>
      </Grid>
    );
  }

  return (
    <Grid fullWidth>
      <Column lg={16} md={8} sm={4}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Loading withOverlay={false} description="Completing sign in..." />
        </div>
      </Column>
    </Grid>
  );
}

// Made with Bob
